import { useState, useEffect, useRef } from 'react';
import type { StrategyNode } from '../types/BaseNode';
import { ApiService } from '../services/ApiService';
import { serializeNode } from '../utils/serializeNode';
import { updateNodeParam } from '../utils/updateNodeParam';
import type { PendingChange } from './useQuickTuneState';

interface UseStrategyPreviewProps {
    originalRoot: StrategyNode | null;
    changes: Map<string, PendingChange>;
    globalStart: string | null;
    globalEnd: string | null;
}

export function useStrategyPreview({ originalRoot, changes, globalStart, globalEnd }: UseStrategyPreviewProps) {
    const [previewRoot, setPreviewRoot] = useState<StrategyNode | null>(null);
    const [previewResult, setPreviewResult] = useState<any[] | null>(null);
    const [isEvaluated, setIsEvaluated] = useState(false);

    // Track the last evaluated tree structure to prevent redundant API calls
    const lastSerializedTree = useRef<string | null>(null);

    // 1. Calculate the preview tree whenever changes occur
    useEffect(() => {
        if (!originalRoot) {
            setPreviewRoot(null);
            setPreviewResult(null);
            setIsEvaluated(false);
            lastSerializedTree.current = null;
            return;
        }

        if (changes.size === 0) {
            // Short circuit: if no changes, we don't need a preview
            setPreviewRoot(originalRoot);
            setPreviewResult(null);
            setIsEvaluated(false);
            lastSerializedTree.current = null;
            return;
        }

        let currentTree = originalRoot;
        changes.forEach(change => {
            const { nodeId, paramKey, newValue } = change;
            currentTree = updateNodeParam(currentTree, nodeId, paramKey, newValue) || currentTree;
        });

        // Only update previewRoot if the structure actually changed
        const serialized = JSON.stringify(currentTree);
        if (serialized !== lastSerializedTree.current) {
            lastSerializedTree.current = serialized;
            setPreviewRoot(currentTree);
            // If no changes, we don't need to evaluate - the parent component should use its own result
            setIsEvaluated(changes.size === 0);
            if (changes.size === 0) {
                setPreviewResult(null);
            }
        }

    }, [originalRoot, changes]);

    // 2. Trigger evaluation when previewRoot changes
    // evaluation cycle (only if dirty)
    useEffect(() => {
        if (!previewRoot || isEvaluated || changes.size === 0 || previewRoot === originalRoot) {
            setPreviewResult(null); // Ensure result is null if not evaluating
            return;
        }

        let mounted = true;

        const evaluate = async () => {
            try {
                const serialized = serializeNode(previewRoot, globalStart, globalEnd);
                const json = await ApiService.evaluateStrategy(serialized, {
                    start_date: globalStart,
                    end_date: globalEnd
                });
                if (mounted && json.data) {
                    setPreviewResult(json.data);
                }
            } catch (err) {
                console.error("Preview evaluation failed", err);
            } finally {
                if (mounted) setIsEvaluated(true);
            }
        };

        evaluate();

        return () => { mounted = false; };
    }, [previewRoot, originalRoot, globalStart, globalEnd]);

    return {
        previewRoot,
        previewResult,
        isEvaluated
    };
}
